import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PaymentService from '../../services/PaymentService';

const CallbackContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const CallbackCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #5f259f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StatusIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 40px;
  
  &.success {
    background: #d4edda;
    color: #28a745;
  }
  
  &.failed {
    background: #f8d7da;
    color: #dc3545;
  }
  
  &.pending {
    background: #fff3cd;
    color: #ffc107;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 12px;
`;

const Message = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
`;

const TransactionDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #666;
  font-size: 13px;
`;

const DetailValue = styled.span`
  color: #333;
  font-size: 13px;
  font-weight: 500;
`;

const Button = styled.button`
  padding: 14px 32px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 8px;

  &.primary {
    background: #5f259f;
    color: white;
    
    &:hover {
      background: #4a1d7a;
    }
  }

  &.secondary {
    background: #f0f0f0;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  }
`;

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed, pending
  const [transactionData, setTransactionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Get transaction details from URL params (set by PhonePe redirect)
      const transactionId = searchParams.get('transactionId');
      const merchantTransactionId = searchParams.get('merchantTransactionId');
      const code = searchParams.get('code');

      if (!transactionId && !merchantTransactionId) {
        throw new Error('Invalid callback. Missing transaction details.');
      }

      // Verify payment status with backend
      const response = await PaymentService.verifyPayment({
        transactionId: transactionId || merchantTransactionId,
        code
      });

      if (response.success) {
        setTransactionData(response.data);
        setStatus(response.data.status);
      } else {
        throw new Error(response.message || 'Payment verification failed');
      }
    } catch (err) {
      setError(err.message);
      setStatus('failed');
    }
  };

  const handleClose = () => {
    // Get original callback URL from transaction data or search params
    const callbackUrl = transactionData?.callbackUrl || searchParams.get('callbackUrl');
    
    if (window.opener) {
      // Send message to parent window
      window.opener.postMessage({
        type: 'WILLOVATE_PAYMENT_COMPLETE',
        status: status,
        transactionId: transactionData?.transactionId,
        orderId: transactionData?.orderId,
        amount: transactionData?.amount
      }, '*');
      window.close();
    } else if (callbackUrl) {
      // Redirect to callback URL with status
      const url = new URL(callbackUrl);
      url.searchParams.set('status', status);
      url.searchParams.set('transactionId', transactionData?.transactionId || '');
      url.searchParams.set('orderId', transactionData?.orderId || '');
      window.location.href = url.toString();
    } else {
      // Navigate to home or close
      navigate('/');
    }
  };

  const handleRetry = () => {
    // Redirect back to payment page with original params
    if (transactionData?.orderId && transactionData?.amount) {
      navigate(`/payment?orderId=${transactionData.orderId}&amount=${transactionData.amount}&appName=${transactionData.appName || ''}`);
    } else {
      navigate('/');
    }
  };

  if (status === 'verifying') {
    return (
      <CallbackContainer>
        <CallbackCard>
          <LoadingSpinner />
          <Title>Verifying Payment</Title>
          <Message>Please wait while we confirm your payment status...</Message>
        </CallbackCard>
      </CallbackContainer>
    );
  }

  return (
    <CallbackContainer>
      <CallbackCard>
        <StatusIcon className={status}>
          {status === 'success' && '✓'}
          {status === 'failed' && '✗'}
          {status === 'pending' && '⏳'}
        </StatusIcon>
        
        <Title>
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Payment Failed'}
          {status === 'pending' && 'Payment Pending'}
        </Title>
        
        <Message>
          {status === 'success' && 'Your payment has been processed successfully.'}
          {status === 'failed' && (error || 'Your payment could not be processed.')}
          {status === 'pending' && 'Your payment is being processed. We will notify you once confirmed.'}
        </Message>

        {transactionData && (
          <TransactionDetails>
            {transactionData.transactionId && (
              <DetailRow>
                <DetailLabel>Transaction ID</DetailLabel>
                <DetailValue>{transactionData.transactionId}</DetailValue>
              </DetailRow>
            )}
            {transactionData.orderId && (
              <DetailRow>
                <DetailLabel>Order ID</DetailLabel>
                <DetailValue>{transactionData.orderId}</DetailValue>
              </DetailRow>
            )}
            {transactionData.amount && (
              <DetailRow>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue>₹ {transactionData.amount.toLocaleString('en-IN')}</DetailValue>
              </DetailRow>
            )}
            {transactionData.paymentMethod && (
              <DetailRow>
                <DetailLabel>Payment Method</DetailLabel>
                <DetailValue>{transactionData.paymentMethod}</DetailValue>
              </DetailRow>
            )}
            {transactionData.timestamp && (
              <DetailRow>
                <DetailLabel>Date & Time</DetailLabel>
                <DetailValue>{new Date(transactionData.timestamp).toLocaleString()}</DetailValue>
              </DetailRow>
            )}
          </TransactionDetails>
        )}

        <div>
          {status === 'failed' && (
            <Button className="secondary" onClick={handleRetry}>
              Try Again
            </Button>
          )}
          <Button className="primary" onClick={handleClose}>
            {status === 'success' ? 'Done' : 'Close'}
          </Button>
        </div>
      </CallbackCard>
    </CallbackContainer>
  );
};

export default PaymentCallback;
